import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import { gql } from "generated-graphql";
import { RuleSuggestionStatus } from "generated-graphql/graphql";
import { useState } from "react";

const GET_RULE_SUGGESTION = gql(`
  query RuleSuggestion($id: ID!) {
    ruleSuggestion(id: $id) {
      id
      source
      title
      observation
      recommendation
      jurisdiction
      confidence
      provenance
      status
      dismissedAt
      dismissedBy
      dismissReason
      promotedAt
      promotedBy
      linearIssueIdentifier
      linearIssueUrl
      linearStateName
      linearStateType
      linearSyncedAt
      firstSuggestedAt
      lastSuggestedAt
    }
  }
`);

const PROMOTE_RULE_SUGGESTION = gql(`
  mutation PromoteRuleSuggestion($id: ID!) {
    promoteRuleSuggestion(id: $id) {
      id
      status
      promotedAt
      promotedBy
      linearIssueIdentifier
      linearIssueUrl
      linearStateName
      linearStateType
      linearSyncedAt
    }
  }
`);

const REOPEN_RULE_SUGGESTION = gql(`
  mutation ReopenRuleSuggestion($id: ID!) {
    reopenRuleSuggestion(id: $id) {
      id
      status
      dismissedAt
      dismissedBy
      dismissReason
    }
  }
`);

import { confidenceColor, deriveSuggestionStatus } from "./derivedStatus";
import { DismissSuggestionDialog } from "./DismissSuggestionDialog";
import { provenanceRendererFor } from "./provenance";

type Props = {
  /** Suggestion to show, or null when the drawer is closed. Driven by ?suggestionId=. */
  suggestionId: string | null;
  onClose: () => void;
};

export function RuleSuggestionDrawer({ suggestionId, onClose }: Props) {
  const alerts = useAlerts();
  const [confirmPromote, setConfirmPromote] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);

  const open = suggestionId !== null;

  const { data, loading, error } = useQuery(GET_RULE_SUGGESTION, {
    variables: { id: suggestionId ?? "" },
    skip: !suggestionId,
    fetchPolicy: "cache-and-network",
  });

  const suggestion = data?.ruleSuggestion ?? null;
  const notFound = Boolean(suggestionId && data && !suggestion && !error);

  const [promote, { loading: promoting }] = useMutation(PROMOTE_RULE_SUGGESTION, {
    refetchQueries: ["RuleSuggestions"],
    awaitRefetchQueries: true,
  });
  const [reopen, { loading: reopening }] = useMutation(REOPEN_RULE_SUGGESTION, {
    refetchQueries: ["RuleSuggestions"],
    awaitRefetchQueries: true,
  });

  const handlePromote = async () => {
    if (!suggestion) return;
    try {
      const result = await promote({ variables: { id: suggestion.id } });
      const identifier = result.data?.promoteRuleSuggestion.linearIssueIdentifier;
      alerts.success(identifier ? `Ticket ${identifier} created.` : "Ticket created.");
      setConfirmPromote(false);
    } catch (e) {
      alerts.error("Failed to promote the suggestion.", e);
    }
  };

  const handleReopen = async () => {
    if (!suggestion) return;
    try {
      await reopen({ variables: { id: suggestion.id } });
      alerts.success("Suggestion reopened.");
    } catch (e) {
      alerts.error("Failed to reopen the suggestion.", e);
    }
  };

  const derived = suggestion
    ? deriveSuggestionStatus(
        suggestion.status,
        suggestion.linearStateName,
        suggestion.linearStateType,
        suggestion.linearIssueIdentifier
      )
    : null;

  // Promotable when open, or when a prior promotion was canceled / never
  // produced a ticket — matching the server-side promotable guard.
  const canPromote =
    suggestion?.status === RuleSuggestionStatus.Open ||
    (suggestion?.status === RuleSuggestionStatus.Promoted &&
      (suggestion.linearStateType === "canceled" || suggestion.linearIssueIdentifier === null));

  const ProvenanceRenderer = suggestion ? provenanceRendererFor(suggestion.source) : null;

  const footer = suggestion ? (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      {suggestion.status === RuleSuggestionStatus.Dismissed ? (
        <Button onClick={handleReopen} loading={reopening}>
          Reopen
        </Button>
      ) : null}
      {suggestion.status === RuleSuggestionStatus.Open ? (
        <Button color="inherit" onClick={() => setDismissOpen(true)}>
          Dismiss
        </Button>
      ) : null}
      {canPromote ? (
        <Button variant="contained" onClick={() => setConfirmPromote(true)}>
          Promote
        </Button>
      ) : null}
    </Stack>
  ) : null;

  return (
    <>
      {/* nonModal: the grid stays clickable behind the drawer, so triage flows
          row → drawer → next row without closing (URL param retargets it). */}
      <RightDrawerShell
        open={open}
        onClose={onClose}
        title={suggestion?.title ?? "Suggestion"}
        footer={footer}
        nonModal
      >
        {error && (
          <Alert severity="error">
            Failed to load this suggestion. Close the drawer and try again.
          </Alert>
        )}
        {notFound && !loading && (
          <Alert severity="error">
            This rule suggestion could not be found. It may have been removed, or the link may be
            stale.
          </Alert>
        )}
        {loading && !suggestion && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {suggestion && derived && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              <StatusChip label={derived.label} color={derived.color} />
              {suggestion.confidence ? (
                <StatusChip
                  label={suggestion.confidence}
                  color={confidenceColor(suggestion.confidence)}
                />
              ) : null}
            </Stack>
            {suggestion.jurisdiction ? (
              <Typography variant="body2" color="text.secondary">
                {suggestion.jurisdiction}
              </Typography>
            ) : null}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Observation
              </Typography>
              <Typography variant="body2">{suggestion.observation}</Typography>
            </Box>
            {suggestion.recommendation ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Suggested rule behavior
                </Typography>
                <Typography variant="body2">{suggestion.recommendation}</Typography>
              </Box>
            ) : null}
            {suggestion.linearIssueUrl && suggestion.linearIssueIdentifier ? (
              <MuiLink href={suggestion.linearIssueUrl} target="_blank" rel="noreferrer">
                {suggestion.linearIssueIdentifier} — {derived.label}
              </MuiLink>
            ) : null}
            {suggestion.status === RuleSuggestionStatus.Dismissed && suggestion.dismissReason ? (
              <Typography variant="body2" color="text.secondary">
                Dismissed: {suggestion.dismissReason}
              </Typography>
            ) : null}
            {ProvenanceRenderer ? <ProvenanceRenderer provenance={suggestion.provenance} /> : null}
          </Stack>
        )}
      </RightDrawerShell>

      <ConfirmDialog
        open={confirmPromote}
        loading={promoting}
        onConfirm={handlePromote}
        onClose={() => setConfirmPromote(false)}
        header="Promote to engineering ticket?"
        info="This creates a Linear ticket in the Engineering Triage queue with this suggestion's evidence attached. An engineer will review and hand-write the Compliance Engine rule."
        confirmText="Create ticket"
      />
      {suggestion ? (
        <DismissSuggestionDialog
          open={dismissOpen}
          suggestionId={suggestion.id}
          onClose={() => setDismissOpen(false)}
          onDone={onClose}
        />
      ) : null}
    </>
  );
}
